import { useCallback, useEffect, useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { soggetti as apiSoggetti, type DatiSoggetto } from '../api/scadenziario.ts';
import type { Soggetto, TipoSoggetto } from '../api/tipi-dati.ts';
import { Dialogo } from '../componenti/Dialogo.tsx';
import {
  DialogoEliminazione,
  ZonaPericolosa,
  type BersaglioEliminazione,
} from '../componenti/DialogoEliminazione.tsx';
import { useAutenticazione } from '../auth/contesto.tsx';
import { IntestazioneOrdinabile, useOrdinamento } from '../componenti/IntestazioneOrdinabile.tsx';
import { ordinaRighe, type Valore } from '../componenti/ordinamento.ts';
import { puoRedigere } from '../auth/ruoli.ts';

const TIPI: { valore: TipoSoggetto; etichetta: string; nota: string }[] = [
  { valore: 'PERSONA', etichetta: 'Persona', nota: 'dipendenti: attestati, corsi, visite mediche' },
  { valore: 'ATTREZZATURA', etichetta: 'Attrezzatura', nota: 'mezzi e macchinari: revisioni, tarature' },
  { valore: 'SEDE', etichetta: 'Sede', nota: 'impianti e locali' },
  { valore: 'AZIENDA', etichetta: 'Azienda', nota: 'adempimenti societari' },
];

/**
 * ⚠️ Il tipo si ordina per l'**etichetta che si legge** e non per il codice
 * salvato: a video c'è «Attrezzatura», non `ATTREZZATURA`, e ordinare sul
 * secondo darebbe un ordine che non corrisponde a quello che si vede.
 *
 * Codice, reparto ed email sono facoltativi: quando mancano finiscono in
 * fondo, in salita come in discesa (vedi `ordinamento.ts`).
 */
function valoreSoggetto(s: Soggetto, chiave: string): Valore {
  switch (chiave) {
    case 'tipo': return TIPI.find((t) => t.valore === s.tipo)?.etichetta ?? s.tipo;
    case 'codice': return s.codice;
    case 'denominazione': return s.denominazione;
    case 'reparto': return s.reparto;
    case 'email': return s.email;
    case 'aperte': return s.scadenzeAperte ?? 0;
    default: return null;
  }
}

export function Soggetti() {
  const { ordinamento, ordina } = useOrdinamento();
  const { utente } = useAutenticazione();
  const puoEliminare = puoRedigere(utente?.ruolo);

  const [elementi, setElementi] = useState<Soggetto[]>([]);
  const [totale, setTotale] = useState(0);
  const [tipo, setTipo] = useState('');
  const [cerca, setCerca] = useState('');
  const [mostraDisattivati, setMostraDisattivati] = useState(false);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [conferma, setConferma] = useState<string | null>(null);

  /** `undefined` = chiuso · `null` = nuovo · oggetto = modifica. */
  const [inForm, setInForm] = useState<Soggetto | null | undefined>(undefined);
  /** Il soggetto per cui è aperta la conferma di cancellazione definitiva. */
  const [daEliminare, setDaEliminare] = useState<BersaglioEliminazione | null>(null);
  const [ricarica, setRicarica] = useState(0);

  useEffect(() => {
    let annullato = false;
    setInCaricamento(true);

    apiSoggetti
      .elenca({
        ...(tipo !== '' ? { tipo } : {}),
        ...(cerca !== '' ? { cerca } : {}),
        ...(mostraDisattivati ? {} : { attivo: 'true' }),
        limite: 500,
      })
      .then((r) => { if (!annullato) { setElementi(r.elementi); setTotale(r.totale); setErrore(null); } })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [tipo, cerca, mostraDisattivati, ricarica]);

  const dopoAzione = useCallback((messaggio: string) => {
    setInForm(undefined);
    setDaEliminare(null);
    setConferma(messaggio);
    setRicarica((n) => n + 1);
  }, []);

  async function cambiaStato(s: Soggetto) {
    try {
      if (s.attivo) await apiSoggetti.disattiva(s.id);
      else await apiSoggetti.riattiva(s.id);
      dopoAzione(`«${s.denominazione}» ${s.attivo ? 'disattivato' : 'riattivato'}.`);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    }
  }

  return (
    <>
      <div className="titolo-pagina">
        <h1>Soggetti</h1>
        <span className="conteggio">{totale} registrati</span>
        <button
          type="button"
          className="bottone bottone-principale azione-titolo"
          onClick={() => setInForm(null)}
        >
          <span aria-hidden="true">+</span> Nuovo soggetto
        </button>
      </div>

      <p className="sottotitolo-pagina">
        Un soggetto è ciò a cui una scadenza appartiene: un dipendente, un mezzo, una sede o
        l'azienda stessa. Si registra una volta e poi lo si ritrova in ogni scadenza che lo
        riguarda, con lo storico attaccato.
      </p>

      {conferma !== null && (
        <div className="avviso-esito" role="status">
          {conferma}
          <button type="button" className="bottone-piatto" onClick={() => setConferma(null)}>Chiudi</button>
        </div>
      )}
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <div className="filtri">
        <div className="campo">
          <label htmlFor="s-tipo">Tipo</label>
          <select id="s-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Tutti i tipi</option>
            {TIPI.map((t) => <option key={t.valore} value={t.valore}>{t.etichetta}</option>)}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="s-cerca">Cerca</label>
          <input
            id="s-cerca"
            type="text"
            placeholder="nome, codice o reparto"
            defaultValue={cerca}
            onBlur={(e) => setCerca(e.target.value.trim())}
            onKeyDown={(e) => { if (e.key === 'Enter') setCerca(e.currentTarget.value.trim()); }}
          />
        </div>

        <label className="interruttore">
          <input
            type="checkbox"
            checked={mostraDisattivati}
            onChange={(e) => setMostraDisattivati(e.target.checked)}
          />
          Mostra anche i disattivati
        </label>
      </div>

      <div className="pannello">
        {inCaricamento ? (
          <p className="stato-caricamento">Caricamento…</p>
        ) : elementi.length === 0 ? (
          <p className="stato-vuoto">Nessun soggetto trovato.</p>
        ) : (
          <div className="tabella-contenitore">
            <table className="tabella-centrata">
              <caption className="solo-lettori-schermo">
                Anagrafica dei soggetti a cui sono intestate le scadenze
              </caption>
              <thead>
                <tr>
                  <IntestazioneOrdinabile chiave="tipo" ordinamento={ordinamento} onOrdina={ordina}>Tipo</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="codice" ordinamento={ordinamento} onOrdina={ordina}>Codice</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="denominazione" ordinamento={ordinamento} onOrdina={ordina}>Denominazione</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="reparto" ordinamento={ordinamento} onOrdina={ordina}>Reparto</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="email" ordinamento={ordinamento} onOrdina={ordina}>Email</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="aperte" ordinamento={ordinamento} onOrdina={ordina}>Scadenze aperte</IntestazioneOrdinabile>
                  <th scope="col" className="col-azione">Azione</th>
                </tr>
              </thead>
              <tbody>
                {ordinaRighe(elementi, ordinamento, valoreSoggetto).map((s) => (
                  <tr key={s.id} className={s.attivo ? '' : 'riga-annullata'}>
                    <td className="tipo-nome">{TIPI.find((t) => t.valore === s.tipo)?.etichetta ?? s.tipo}</td>
                    <td className="data">{s.codice ?? '—'}</td>
                    <td>
                      <span className="soggetto-nome">{s.denominazione}</span>
                      {!s.attivo && <span className="badge badge-annullata" style={{ marginLeft: 8 }}>
                        <span aria-hidden="true">⊘</span> Disattivato
                      </span>}
                    </td>
                    <td className="tipo-nome">{s.reparto ?? '—'}</td>
                    <td className="tipo-nome">{s.email ?? '—'}</td>
                    <td className="numero">{s.scadenzeAperte ?? 0}</td>
                    <td className="col-azione">
                      <div className="cella-azioni">
                        <button type="button" className="bottone" onClick={() => setInForm(s)}>
                          <span aria-hidden="true">✎</span> Modifica
                        </button>
                        <button type="button" className="bottone" onClick={() => void cambiaStato(s)}>
                          {s.attivo ? 'Disattiva' : 'Riattiva'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inForm !== undefined && (
        <DialogoSoggetto
          soggetto={inForm}
          puoEliminare={puoEliminare}
          onChiudi={() => setInForm(undefined)}
          onSalvato={dopoAzione}
          // Si chiude la scheda prima di aprire la conferma: due dialoghi
          // modali sovrapposti si contenderebbero il fuoco della tastiera, e
          // il tasto Esc chiuderebbe quello sbagliato.
          onElimina={(s) => {
            setInForm(undefined);
            setDaEliminare({
              entita: 'soggetto',
              id: s.id,
              nome: s.denominazione,
              etichettaNome: 'la denominazione del soggetto',
            });
          }}
        />
      )}

      {daEliminare !== null && (
        <DialogoEliminazione
          bersaglio={daEliminare}
          onChiudi={() => setDaEliminare(null)}
          onEliminato={dopoAzione}
        />
      )}
    </>
  );
}

function DialogoSoggetto({
  soggetto, puoEliminare, onChiudi, onSalvato, onElimina,
}: {
  soggetto: Soggetto | null;
  puoEliminare: boolean;
  onChiudi: () => void;
  onSalvato: (messaggio: string) => void;
  onElimina: (soggetto: Soggetto) => void;
}) {
  const [tipo, setTipo] = useState<TipoSoggetto>(soggetto?.tipo ?? 'PERSONA');
  const [codice, setCodice] = useState(soggetto?.codice ?? '');
  const [denominazione, setDenominazione] = useState(soggetto?.denominazione ?? '');
  const [reparto, setReparto] = useState(soggetto?.reparto ?? '');
  const [email, setEmail] = useState(soggetto?.email ?? '');
  const [note, setNote] = useState(soggetto?.note ?? '');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const notaTipo = TIPI.find((t) => t.valore === tipo)?.nota ?? '';

  async function salva() {
    setErrore(null);
    setInCorso(true);

    const dati: DatiSoggetto = {
      tipo,
      codice: codice.trim() === '' ? null : codice.trim(),
      denominazione: denominazione.trim(),
      reparto: reparto.trim() === '' ? null : reparto.trim(),
      email: email.trim() === '' ? null : email.trim(),
      note: note.trim() === '' ? null : note.trim(),
    };

    try {
      if (soggetto === null) {
        const creato = await apiSoggetti.crea(dati);
        onSalvato(`Creato «${creato.denominazione}».`);
      } else {
        await apiSoggetti.modifica(soggetto.id, dati);
        onSalvato(`«${dati.denominazione}» aggiornato.`);
      }
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  return (
    <Dialogo titolo={soggetto === null ? 'Nuovo soggetto' : 'Modifica soggetto'} aperto onChiudi={onChiudi}>
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <form onSubmit={(e) => { e.preventDefault(); void salva(); }}>
        <div className="campo">
          <label htmlFor="d-tipo">Tipo <span className="obbligatorio">obbligatorio</span></label>
          <select id="d-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoSoggetto)}>
            {TIPI.map((t) => <option key={t.valore} value={t.valore}>{t.etichetta}</option>)}
          </select>
          <p className="aiuto">{notaTipo}</p>
        </div>

        <div className="campo">
          <label htmlFor="d-denominazione">
            Denominazione <span className="obbligatorio">obbligatoria</span>
          </label>
          <input
            id="d-denominazione"
            type="text"
            value={denominazione}
            onChange={(e) => setDenominazione(e.target.value)}
            placeholder="Nome e cognome, oppure descrizione del bene"
            required
            autoFocus
          />
        </div>

        <div className="campi-affiancati">
          <div className="campo">
            <label htmlFor="d-codice">Codice <span className="tenue">facoltativo</span></label>
            <input
              id="d-codice"
              type="text"
              value={codice}
              onChange={(e) => setCodice(e.target.value)}
              placeholder="matricola, targa, inventario"
            />
            <p className="aiuto">Se indicato dev'essere unico: serve a riconoscere il soggetto.</p>
          </div>

          <div className="campo">
            <label htmlFor="d-reparto">Reparto <span className="tenue">facoltativo</span></label>
            <input id="d-reparto" type="text" value={reparto} onChange={(e) => setReparto(e.target.value)} />
          </div>
        </div>

        <div className="campo">
          <label htmlFor="d-email">Email <span className="tenue">facoltativa</span></label>
          <input id="d-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <p className="aiuto">
            Se presente, l'interessato può ricevere direttamente gli avvisi di scadenza.
          </p>
        </div>

        <div className="campo">
          <label htmlFor="d-note">Note <span className="tenue">facoltative</span></label>
          <textarea id="d-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <div className="dialogo-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inCorso}>
            {inCorso ? 'Salvataggio…' : soggetto === null ? 'Crea soggetto' : 'Salva modifiche'}
          </button>
          <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inCorso}>
            Annulla
          </button>
        </div>
      </form>

      {/* Fuori dal form e in fondo a tutto: ci si arriva solo aprendo la scheda
          di un soggetto preciso e scorrendo oltre «Salva modifiche». Su un
          soggetto nuovo non c'è niente da cancellare. */}
      {soggetto !== null && puoEliminare && (
        <ZonaPericolosa
          spiegazione={
            'Toglie il soggetto dall\'anagrafica per sempre. Se ha scadenze collegate verrà ' +
            'chiesto che fine devono fare: sparire con lui, oppure restare nello storico ' +
            'orfane, col nome congelato sulla riga. ' +
            "Per un dipendente che se ne va o un mezzo dismesso la risposta giusta resta " +
            '«Disattiva», che lo toglie dai menu lasciando i collegamenti al loro posto.'
          }
          onElimina={() => onElimina(soggetto)}
        />
      )}
    </Dialogo>
  );
}
